import type { ReactNode, ComponentType } from "react";
import { Icon } from "../Icons/Icon";
import {
  alignClasses,
  colorClasses,
  sizeClasses,
  weightClasses,
  type TextAlign,
  type TextColor,
  type TextSize,
  type TextWeight,
} from "./textClasses";

interface TextProps {
  size?: TextSize;
  weight?: TextWeight;
  color?: TextColor;
  align?: TextAlign;
  mono?: boolean;
  /** Preserve line breaks in the text (user-entered notes, evidence, etc.). */
  multiline?: boolean;
  /**
   * Clip to a single line with an ellipsis. Use for long values that would
   * otherwise dominate a form row (e.g. a resolved NMFC description) — pair it
   * with a Tooltip so the full text is still reachable. Includes `min-w-0` so
   * it shrinks correctly as a flex child rather than overflowing its Row.
   */
  truncate?: boolean;
  /**
   * Keep the text on one line. Use for a value that reads wrong when broken —
   * a money amount and its currency, a date, an identifier. Unlike `truncate`
   * nothing is hidden; the text simply overflows if the column is too narrow.
   */
  nowrap?: boolean;
  /** Strike the text through (completed todos, superseded values). */
  strike?: boolean;
  icon?: ComponentType<{ className?: string }>;
  /** Selector hook for tests, when the text itself is the thing being asserted. */
  testId?: string | undefined;
  children: ReactNode;
}

export function T({
  size,
  weight,
  color = "default",
  align,
  mono,
  multiline,
  truncate,
  nowrap,
  strike,
  icon: IconProp,
  testId,
  children,
}: TextProps) {
  const classes = [
    size ? sizeClasses[size] : "",
    weight ? weightClasses[weight] : "",
    colorClasses[color],
    align ? alignClasses[align] : "",
    mono ? "font-mono" : "",
    multiline ? "whitespace-pre-wrap wrap-break-word" : "",
    truncate ? "block min-w-0 truncate" : "",
    nowrap ? "whitespace-nowrap" : "",
    strike ? "line-through" : "",
    IconProp ? "inline-flex items-center gap-1.5" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes} data-testid={testId}>
      {IconProp && <Icon icon={IconProp} size="sm" />}
      {children}
    </span>
  );
}
